/**
 * useTaxonomy Hooks (S1.1)
 *
 * ahooks data hooks + mutation hooks for the 5-capable category tree.
 * Cache contract: static `/api/taxonomy/*` cacheKeys; every mutation
 * invalidates them via clearCache (store-first contract — components never
 * call bare ahooks refresh(); they use the wrapped refresh returned here).
 */

import { useCallback, useState } from 'react';
import { useRequest, clearCache } from 'ahooks';
import type { FieldValues, Path, UseFormSetError, UseFormSetFocus } from 'react-hook-form';
import { taxonomyApi } from '@/services/api/taxonomy.api';
import { selectableCategories } from '@/utils/categoryUtils';
import type {
  CategoryGuardError,
  CategoryLevelConfig,
  CreateProductCategoryRequest,
  DeactivateProductCategoryRequest,
  ProductCategory,
  UpdateCategoryLevelRequest,
  UpdateProductCategoryRequest,
} from '@/types/api/productCategory';

const TREE_CACHE_KEY = '/api/taxonomy/categories';
const LEVELS_CACHE_KEY = '/api/taxonomy/levels';
const AUDIT_CACHE_KEY = '/api/taxonomy/audit';

/**
 * Invalidate every taxonomy cache (tree + levels + audit lens).
 *
 * NOTE: exact keys on purpose — ahooks' runtime `clearCache` only accepts
 * a key (or key array); the callback form used elsewhere in the repo is a
 * silent no-op (`Map.delete(fn)`), which leaves stale caches alive within
 * `staleTime`. Static keys make exact-key clearing reliable.
 */
export function invalidateTaxonomyCaches() {
  clearCache([TREE_CACHE_KEY, LEVELS_CACHE_KEY, AUDIT_CACHE_KEY]);
}

// ---------------------------------------------------------------------------
// Pure tree helpers (client-side prediction of the S0.4 handler guards —
// used for the drag depth-cap toast and replacement-option filtering).
// Rule bodies live in @senditship/domain; re-exported so existing
// `@/hooks/useTaxonomy` importers keep resolving them from here — one
// source, no drift.
// ---------------------------------------------------------------------------

export { subtreeIds, subtreeHeight } from '@senditship/domain/rules/productCategory';

/**
 * Localized breadcrumb along the node's materialized path (root › … › self).
 * `separator` defaults to the ' › ' breadcrumb chevron; the search-first picker
 * passes ' / ' via {@link buildPathLabel} — one traversal, two presentations.
 */
export function buildBreadcrumb(
  nodes: ProductCategory[],
  categoryId: string,
  lang: 'en' | 'zh',
  separator = ' › '
): string {
  const target = nodes.find((n) => n.id === categoryId);
  if (!target) return '';
  const byId = new Map(nodes.map((n) => [n.id, n]));
  return target.path
    .split('/')
    .map((id) => {
      const node = byId.get(id);
      if (!node) return id;
      return lang === 'zh' ? node.nameZh : node.nameEn;
    })
    .join(separator);
}

/**
 * Full '/'-joined path label for the search-first taxonomy picker (Slice 4.1),
 * e.g. "車輛系統 / 煞車系統 / 煞車油路". Same traversal as {@link buildBreadcrumb}
 * with the picker's ' / ' separator. Works for ANY node id — including a node on
 * a hidden/disabled level — so an already-selected value always renders its full
 * breadcrumb even though {@link selectablePathOptions} excludes hidden-level
 * nodes from NEW selection (hidden-level read rule). The UI (Slice 4.2) renders
 * this label with middle-truncation that always keeps the leaf + tooltip-on-
 * overflow; the data here stays the full, untruncated path.
 */
export function buildPathLabel(nodes: ProductCategory[], id: string, lang: 'en' | 'zh'): string {
  return buildBreadcrumb(nodes, id, lang, ' / ');
}

/** One search-first picker option: id value + full path label + folded search text. */
export interface CategoryPathOption {
  /** Node id — the value the picker commits (leaf-attach, non-leaf soft-warn upstream). */
  value: string;
  /**
   * Full, untruncated '/'-joined localized path. Slice 4.2 renders it with
   * middle-truncation keeping the leaf + tooltip-on-overflow at the true
   * ~280–320px side-context width; the tooltip and search use the full string.
   */
  label: string;
  /**
   * `code + nameEn + nameZh + path` lower-cased — the UNTRUNCATED path always
   * drives search, so a query on a code, either-language leaf name, or an
   * ancestor name all match.
   */
  searchText: string;
}

/**
 * Options for the search-first taxonomy combobox (Slice 4.2), built on the ONE
 * shippable `selectableCategories` predicate (active nodes at enabled levels —
 * the identical set the mock serves for `?forSelection=true`, asserted for
 * parity in the taxonomy tests). Leaf-preferred: every selectable node becomes
 * one option carrying its full path label.
 */
export function selectablePathOptions(
  nodes: ProductCategory[],
  levels: CategoryLevelConfig[],
  lang: 'en' | 'zh'
): CategoryPathOption[] {
  return selectableCategories(nodes, levels).map((node) => {
    const label = buildPathLabel(nodes, node.id, lang);
    const searchText = [node.code, node.nameEn, node.nameZh, label].join(' ').toLowerCase();
    return { value: node.id, label, searchText };
  });
}

/** Extract the machine-readable guard code from an axios error, if present. */
export function extractGuardError(error: unknown): CategoryGuardError | null {
  const guardCodes: CategoryGuardError[] = [
    'PARENT_NOT_FOUND',
    'PARENT_INACTIVE',
    'DEPTH_EXCEEDED',
    'LEVEL_DISABLED',
    'CODE_TAKEN',
    'NODE_NOT_FOUND',
    'CYCLE',
    'SUBTREE_DEPTH_EXCEEDED',
    'REPLACEMENT_REQUIRED',
    'REPLACEMENT_INVALID',
    'COMMENT_REQUIRED',
    'NAV_LABEL_COLLISION',
    'CORE_LEVEL_LOCKED',
  ];
  const code = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
  return code && (guardCodes as string[]).includes(code) ? (code as CategoryGuardError) : null;
}

// ---------------------------------------------------------------------------
// Read hooks
// ---------------------------------------------------------------------------

/**
 * Full tree for the admin surface (always fetches inactive too — the
 * include-inactive toggle filters client-side so the toggle is instant).
 */
export function useTaxonomyTree() {
  const { data, error, loading, run } = useRequest(
    () => taxonomyApi.listCategories({ includeInactive: true }),
    {
      cacheKey: TREE_CACHE_KEY,
      staleTime: 60000,
      cacheTime: 300000,
    }
  );

  return {
    nodes: data?.data ?? [],
    error,
    isLoading: loading,
    /** Invalidate + re-run (the only sanctioned refresh — never bare ahooks refresh in components). */
    refresh: () => {
      invalidateTaxonomyCaches();
      return run();
    },
  };
}

/** The 5 renamable bilingual level labels + L4/L5 enablement. */
export function useCategoryLevels() {
  const { data, error, loading, run } = useRequest(() => taxonomyApi.listLevels(), {
    cacheKey: LEVELS_CACHE_KEY,
    staleTime: 60000,
    cacheTime: 300000,
  });

  const levels: CategoryLevelConfig[] = data?.data ?? [];

  return {
    levels,
    error,
    isLoading: loading,
    refresh: () => {
      invalidateTaxonomyCaches();
      return run();
    },
  };
}

// ---------------------------------------------------------------------------
// Mutations — every one invalidates the /api/taxonomy caches (clearCache-in-mutation)
// ---------------------------------------------------------------------------

export function useTaxonomyMutations() {
  const createNode = async (data: CreateProductCategoryRequest) => {
    const response = await taxonomyApi.createCategory(data);
    invalidateTaxonomyCaches();
    return response;
  };

  const updateNode = async (id: string, data: UpdateProductCategoryRequest) => {
    const response = await taxonomyApi.updateCategory(id, data);
    invalidateTaxonomyCaches();
    return response;
  };

  const reparentNode = async (id: string, newParentId: string | null, comment: string) => {
    const response = await taxonomyApi.reparentCategory(id, { newParentId, comment });
    invalidateTaxonomyCaches();
    return response;
  };

  const deactivateNode = async (id: string, data: DeactivateProductCategoryRequest) => {
    const response = await taxonomyApi.deactivateCategory(id, data);
    invalidateTaxonomyCaches();
    return response;
  };

  /** Batch level update — PATCHes only the levels that actually changed. */
  const updateLevels = async (
    changes: Array<{ level: number; patch: UpdateCategoryLevelRequest }>
  ) => {
    for (const change of changes) {
      await taxonomyApi.updateLevel(change.level, change.patch);
    }
    invalidateTaxonomyCaches();
  };

  return { createNode, updateNode, reparentNode, deactivateNode, updateLevels };
}

// ---------------------------------------------------------------------------
// Shared inline-create submit path (S3.2)
//
// One create+guard path for BOTH root-create (`CreateNodeModal`) and the
// inline quick-create row (S3.3). It wraps `createNode`, maps a `CODE_TAKEN`
// 409 back onto the code field (`setError` + `setFocus` — the dup-guard UX
// preserved verbatim from the old modal), and exposes a `codeTaken` flag for
// the inline row's on-blur availability UI. Callers own their own success /
// non-`CODE_TAKEN` failure messaging via the discriminated result.
// ---------------------------------------------------------------------------

/** Discriminated create result — callers own success / failure messaging. */
export type InlineCreateNodeResult =
  | { ok: true; id: string }
  | { ok: false; guard: CategoryGuardError | null };

export interface UseInlineCreateNodeOptions<T extends FieldValues & { code: string }> {
  /** The consuming form's RHF `setError` — marks the code field on CODE_TAKEN. */
  setError: UseFormSetError<T>;
  /** The consuming form's RHF `setFocus` — refocuses code on CODE_TAKEN. */
  setFocus: UseFormSetFocus<T>;
}

export interface UseInlineCreateNodeResult {
  /** Files the node; resolves to a discriminated {@link InlineCreateNodeResult}. */
  submit: (data: CreateProductCategoryRequest) => Promise<InlineCreateNodeResult>;
  /** True after the last submit hit CODE_TAKEN (drives the inline red-inline UI). */
  codeTaken: boolean;
  /** Clear the `codeTaken` flag (call when the user edits the code again). */
  reset: () => void;
}

export function useInlineCreateNode<T extends FieldValues & { code: string }>({
  setError,
  setFocus,
}: UseInlineCreateNodeOptions<T>): UseInlineCreateNodeResult {
  const { createNode } = useTaxonomyMutations();
  const [codeTaken, setCodeTaken] = useState(false);

  const reset = useCallback(() => setCodeTaken(false), []);

  const submit = async (data: CreateProductCategoryRequest): Promise<InlineCreateNodeResult> => {
    try {
      const response = await createNode(data);
      setCodeTaken(false);
      return { ok: true, id: response.data.id };
    } catch (error) {
      const guard = extractGuardError(error);
      if (guard === 'CODE_TAKEN') {
        // Keep the input, mark + focus the offending field (dup-guard UX contract).
        setCodeTaken(true);
        setError('code' as Path<T>, { type: 'server', message: 'taxonomy:errors.CODE_TAKEN' });
        setFocus('code' as Path<T>);
        return { ok: false, guard };
      }
      return { ok: false, guard };
    }
  };

  return { submit, codeTaken, reset };
}
